<template>
  <footer class="text-white flex flex-col items-center overflow-hidden">
    <div class="w-81vw flex-grow">
      <!-- 顶部：Logo + 按钮 -->
      <div class="flex h-[50%] flex-row justify-between mt-[6%]">
        <!-- Logo + Socials -->
        <div class="flex flex-col">
          <div class="logo ">
            <img src="../assets/logo2.png" alt="Logo" class="h-[80%]" />
          </div>
          <div class="mt-4vw text-center">
            <p class="text-left font-normal text-[1.1vw]">{{ footerTexts.followUs }} :</p>
            <div class="flex mt-[1.6vw] gap-[1.5vw] w-[13vw]">
              <a v-for="(icon, index) in icons" :key="index" :href="icon.url" target="_blank" rel="noopener noreferrer">
                <img :src="icon.src" :alt="icon.alt"
                  class="h-[2vw] transform transition-transform duration-300 ease-in-out hover:-translate-y-[10px]" />
              </a>
            </div>
          </div>
        </div>

        <!-- Middle Buttons -->
        <div class="flex flex-wrap mt-[54px] w-full justify-center">
          <div class="flex gap-[35px] w-full justify-center">
            <button v-for="(btn, index) in buttons" :key="index" @click="handleClick(btn, index)"
              :id="btn.labelKey === 'inquiry' ? 'cta-inquiry-footer' : undefined"
              class="min-w-[12vw] h-[3vw] bg-[#FFA200] rounded-[0.4vw] font-bold text-black text-[1.2vw] flex justify-center items-center hover:bg-[#e69500] whitespace-nowrap px-[1.5vw]">
              {{ getButtonText(btn.labelKey) }}
              <img src="@/assets/arrow.png" alt="Arrow" class="ml-2 h-3" />
            </button>
          </div>
        </div>

        <!-- 菜单 -->
        <div class="w-auto min-w-[10vw] flex flex-col items-end">
          <ul class="list-none space-y-[1.5vw] font-bold text-[0.8vw] xl:text-[1.0vw] cursor-pointer">
            <li v-for="item in menuItems" :key="item.path"
              :style="{
                color: route.path === item.path || (item.path === '/core-offers' && route.path.startsWith('/core-offers')) ? '#FFA200' : 'inherit',
              }"
              class="text-right leading-tight relative whitespace-nowrap">
              <!-- 包裹父菜单和子菜单的容器 -->
              <div v-if="item.path === '/core-offers'"
                @mouseenter="showSubmenu = true"
                @mouseleave="showSubmenu = false"
                class="relative text-right">
                <div @click="navigate(item.path)" class="cursor-pointer text-right">
                  {{ getMenuText(item.labelKey) }}
                </div>
                <!-- 子菜单 - 使用padding-top来填补间隙 -->
                <ul v-if="showSubmenu"
                  class="absolute right-0 top-full pt-[0.5vw] z-50">
                  <div class="bg-white text-black rounded-[0.5vw] shadow-lg py-[0.5vw] px-[1vw] whitespace-nowrap">
                    <li @click.stop="navigate('/core-offers/source-product')"
                      :style="{
                        color: route.path === '/core-offers/source-product' ? '#FFA200' : '#000',
                      }"
                      class="hover:text-[#FFA200] cursor-pointer py-[0.3vw] list-none">
                      {{ menuTexts.sourcingIllustrations }}
                    </li>
                  </div>
                </ul>
              </div>
              <!-- 其他菜单项 -->
              <div v-else
                :id="item.labelKey === 'contactUs' ? 'cta-contact-footer' : undefined"
                class="cursor-pointer text-right"
                @click="item.labelKey === 'contactUs' ? handleContactClick() : navigate(item.path)">
                {{ getMenuText(item.labelKey) }}
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- 底部版权 -->
    <div
      class="w-81vw border-t  border-t-[0.3px] border-white pt-[0.5vw] pb-[0.5vw] flex items-end justify-center text-[14px]   font-normal">
      {{ footerTexts.copyright }}
    </div>
  </footer>
</template>
<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, ref } from "vue";
import { pushToDataLayer } from '../utils/gtm';
import { useI18n } from 'vue-i18n';
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking';

const { locale, messages } = useI18n(); // 移除 t
const route = useRoute();
const router = useRouter();
const showSubmenu = ref(false);

// 获取页脚文本
const footerTexts = computed(() => {
  return messages.value[locale.value]?.footer || {
    copyright: '© 2024 All rights reserved',
    findMore: 'Find More',
    inquiry: 'Inquiry',
    privacyPolicy: 'Privacy Policy',
    followUs: 'Follow Us'
  };
});

// 获取菜单文本
const menuTexts = computed(() => {
  return messages.value[locale.value]?.menu || {
    home: 'Home',
    ourOffers: 'Our Offers',
    aboutUs: 'About Us',
    contactUs: 'Contact Us'
  };
});

// 获取按钮文本的辅助函数
const getButtonText = (labelKey) => {
  if (labelKey === 'findMore') return footerTexts.value.findMore;
  if (labelKey === 'inquiry') return footerTexts.value.inquiry;
  return labelKey;
};

// 获取菜单文本的辅助函数
const getMenuText = (labelKey) => {
  if (labelKey === 'home') return menuTexts.value.home;
  if (labelKey === 'ourOffers') return menuTexts.value.ourOffers;
  if (labelKey === 'aboutUs') return menuTexts.value.aboutUs;
  if (labelKey === 'contactUs') return menuTexts.value.contactUs;
  if (labelKey === 'privacyPolicy') return footerTexts.value.privacyPolicy;
  return labelKey;
};

import { icons } from "../utils/imageData";

const menuItems = [
  { labelKey: "home", path: "/" },
  { labelKey: "ourOffers", path: "/core-offers" },
  { labelKey: "aboutUs", path: "/about" },
  { labelKey: "contactUs", path: "/contact-us" },
  { labelKey: "privacyPolicy", path: "/privacy-policy" },
];

const buttons = [
  { labelKey: "findMore" },
  { labelKey: "inquiry" }
];

const isActive = (path: string) => {
  return computed(() => route.path === path);
};

const navigate = (path: string) => {
  if (path === "/") {
    console.log('Navigating to Home11111');
    // Home 页面，使用 fullpage.js API
    router.push(path).then(() => {
      // 等待 DOM 更新和 fullpage.js 初始化
      setTimeout(() => {
        if (window.fullpage_api) {
          console.log('fullpage_api available, moving to section 1');
          window.fullpage_api.moveTo(1);
        } else {
          console.warn('fullpage_api not available yet');
          // 如果 fullpage_api 还没准备好，使用普通滚动作为备用
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 100); // 给 fullpage.js 一些时间来初始化
    });
  } else {
    // 非 Home 页面
    if (route.path !== path) {
      console.log('Navigating to Home222222');
      router.push(path).then(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    } else {
      console.log('Navigating to Home33333');
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  console.log('foot for home Navigating to:', path);
  pushToDataLayer('click', {
    category: 'footer',
    label: path,
  });
};


function handleClick(btn: { labelKey: string }, index: number) {
  const buttonText = btn.labelKey === 'findMore' ? footerTexts.value.findMore : footerTexts.value.inquiry;
  console.log('Clicked:', buttonText, 'at index', index);

  const targetPaths = ["/core-offers", "/contact-us"];
  const targetPath = targetPaths[index];

  // 如果是 Inquiry 按钮，保存 CTA 信息
  if (btn.labelKey === 'inquiry') {
    saveCTAInfo(CTA_CONFIG.FOOTER_INQUIRY);
  }

  if (route.path === targetPath) {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    router.push(targetPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  pushToDataLayer('click', {
    category: 'footer',
    label: targetPath,
  });
}

// Footer 菜单中的 Contact Us 链接点击
function handleContactClick() {
  saveCTAInfo(CTA_CONFIG.FOOTER_CONTACT);
  navigate('/contact-us');
}
</script>

<style scoped>
/* 小屏幕 sm: 640px 以上 */
</style>
